/**
 * Fetches every page of a GitLab list endpoint and returns the concatenated items.
 *
 * GitLab's `Link` header points directly at the upstream GitLab host, which
 * isn't reachable through this app's BFF proxy (and wouldn't pass the
 * proxy's endpoint allowlist). Pagination is driven instead by the
 * `X-Next-Page` response header, re-requesting the same (proxied) URL with
 * an updated `page` parameter.
 *
 * @param {string} url            Initial request URL, including `per_page`.
 * @param {RequestInit} [options] Passed through to every `fetch` call.
 * @returns {Promise<object[]>}   All items across every page, concatenated.
 */
export async function fetchAllGitLabPages(url, options) {
  const items = [];
  let nextUrl = url;

  while (nextUrl) {
    const resp = await fetch(nextUrl, options);
    if (!resp.ok) {
      throw new Error(`GitLab API error: ${resp.status} ${resp.statusText}`);
    }
    items.push(...(await resp.json()));

    const nextPage = resp.headers.get("X-Next-Page");
    if (!nextPage) break;

    const next = new URL(nextUrl, window.location.origin);
    next.searchParams.set("page", nextPage);
    nextUrl = `${next.pathname}${next.search}`;
  }

  return items;
}
